/**
 * Hello Retail Cart Recoms
 *
 * Renders a Hello Retail recommendation carousel inside the cart drawer,
 * using the most recently added product's URL as the recommendation context.
 *
 * The "most recently added product" path comes from Liquid as
 * cart.items.first.product.url (see snippets/cart-drawer-recoms.liquid):
 * Shopify orders cart.items newest-first in this theme and moves re-added
 * products back to position 0, so the first line item is always the
 * latest-touched. The cart-drawer custom element re-renders its inner
 * Liquid via the Section Rendering API on every cart mutation, so the
 * data-context-path is always fresh by the time cart-drawer:refreshed fires.
 *
 * Lifecycle:
 *   - Initial run on DOMContentLoaded (or immediately if DOM is ready).
 *   - Re-runs on every `cart-drawer:refreshed` event to pick up the new
 *     data-context-path written by the freshly rendered snippet.
 *
 * Cards use the shared HelloRetailHelpers pipeline (cloneTemplate
 * hr-product-card-tpl + CARD_SLOT_FILLERS) — identical look to predictive
 * search, full search, and the helloretail-recoms section, with ONE
 * cart-specific override: productMedia prefers a "thumbnail" packshot
 * image when the product has one (mirrors the shop-the-look convention
 * in snippets/shop-the-look-block.liquid). Falls back to the shared
 * buildMediaHtml behavior when no thumbnail exists.
 *
 * The <scroll-carousel> is built in JS rather than rendered server-side:
 * an empty <scroll-carousel> at connectedCallback time crashes in
 * theme.js preloadImages_fn2 (undefined.querySelectorAll).
 *
 * Markup contract (from snippets/cart-drawer-recoms.liquid):
 *   <div data-cart-drawer-recoms
 *        data-recom-key="<hr-box-key>"
 *        data-context-path="/products/...">
 *     <div data-cart-drawer-recoms-list></div>
 *   </div>
 *
 * Depends on: hello-retail-helpers.js
 *             snippets/hello-retail-templates.liquid (for hr-product-card-tpl)
 */
(function () {
  'use strict';

  if (window.__cartDrawerRecomsLoaded) return;
  window.__cartDrawerRecomsLoaded = true;

  // Generation counter — stale loadRecom responses are discarded when
  // multiple cart updates happen in quick succession.
  var gen = 0;

  function init() {
    var block = document.querySelector('[data-cart-drawer-recoms]');
    if (!block) return;

    var key  = block.dataset.recomKey;
    var path = block.dataset.contextPath;
    var list = block.querySelector('[data-cart-drawer-recoms-list]');

    if (!key || !path || !list) return;

    var thisGen = ++gen;

    window.hrq = window.hrq || [];
    window.hrq.push(['loadRecom', [{
      key: key,
      format: 'json',
      context: { urls: [window.location.origin + path] }
    }], function (result) {
      if (thisGen !== gen) return;

      var response = pickFirstResponse(result);
      if (!response) return;

      var products = response.products;
      if (!products || !products.length) return;

      render(list, products);
      block.style.display = '';
    }]);
  }

  function pickFirstResponse(result) {
    if (Array.isArray(result)) return result[0];
    if (result && Array.isArray(result.responses)) {
      if (result.success === false) {
        console.error('[Cart Recoms] loadRecom failed:', result.message);
        return null;
      }
      return result.responses[0];
    }
    if (result && typeof result === 'object') return result;
    return null;
  }

  function render(listEl, products) {
    var H = window.HelloRetailHelpers;
    if (!H) return;

    // Cart-specific fillers: same as the shared defaults except productMedia,
    // which prefers a "thumbnail" packshot image (see renderThumbnailMedia).
    var fillers = Object.assign({}, H.CARD_SLOT_FILLERS, {
      productMedia: renderThumbnailMedia
    });

    var carousel = document.createElement('scroll-carousel');
    carousel.setAttribute('group-cells', '');
    carousel.setAttribute('allow-drag', '');
    carousel.className = 'product-list product-list--carousel product-list--compact scroll-area';

    products.forEach(function (p) {
      var card = H.cloneTemplate('hr-product-card-tpl');
      if (!card) return;
      H.fillSlots(card, fillers, p);
      carousel.appendChild(card);
    });

    listEl.innerHTML = '';
    listEl.appendChild(carousel);
  }

  /**
   * Custom productMedia filler for cart recoms.
   *
   * Prefers an image whose filename contains "thumbnail" (packshot convention,
   * mirrors snippets/shop-the-look-block.liquid). When a thumbnail exists we
   * render a single-image link (no carousel needed — the packshot IS the pick).
   * Otherwise we defer to the shared HelloRetailHelpers.buildMediaHtml which
   * handles the single-image / multi-image carousel fallback.
   */
  function renderThumbnailMedia(el, p) {
    var H = window.HelloRetailHelpers;
    var tpl = document.getElementById('hr-product-card-tpl');
    var imgClass = (tpl && tpl.dataset.imgClass) ||
      'product-card__image product-card__image--primary object-cover';

    var thumb = pickThumbnailImage(p);
    if (thumb) {
      el.innerHTML =
        '<a href="' + H.escapeHtml(p.url) + '" class="product-card__media" data-instant>' +
          '<img src="' + H.escapeHtml(thumb.src) + '"' +
               ' alt="' + H.escapeHtml(thumb.alt || p.title) + '"' +
               ' class="' + imgClass + '" loading="lazy">' +
        '</a>';
      return;
    }

    // No packshot — default behavior (single image or carousel).
    el.innerHTML = H.buildMediaHtml(p, imgClass);
  }

  /**
   * Scan a HR product's extraDataList.images for one whose filename contains
   * the substring "thumbnail" (case-insensitive). Returns the parsed image
   * object { src, alt } or null when no match / no images.
   *
   * HR stores images as JSON strings inside extraDataList.images. Filename
   * is the last URL segment before the query string, matching the Liquid
   * logic in snippets/shop-the-look-block.liquid.
   */
  function pickThumbnailImage(p) {
    var raw = p && p.extraDataList && p.extraDataList.images;
    if (!raw || !raw.length) return null;

    for (var i = 0; i < raw.length; i++) {
      var img = null;
      try { img = JSON.parse(raw[i]); } catch (e) { continue; }
      if (!img || !img.src) continue;

      var filename = img.src.split('?')[0].split('/').pop() || '';
      if (filename.toLowerCase().indexOf('thumbnail') !== -1) {
        return img;
      }
    }
    return null;
  }

  function boot() {
    if (window.HelloRetailHelpers) {
      init();
    } else {
      document.addEventListener('hr:helpers:ready', init);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // cart-drawer custom element replaces its children on every cart mutation
  // and dispatches this bubbling event. We re-query and re-fire from scratch
  // so the freshly rendered snippet's data-context-path is honored.
  document.addEventListener('cart-drawer:refreshed', init);
})();
